/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { scrollSpyOptions } from '../options/scroll-spy.options';

/**
 * Scroll spy directive.
 *
 * Determines the currently active section by projecting a virtual horizontal scan line into the
 * visible portion of the viewport (i.e. the area between the sticky header and sticky footer,
 * derived from `scroll-padding-top` / `scroll-padding-bottom` on the document root). The section
 * whose clipped rect is closest to that line — by either center or edge distance — wins.
 *
 * When a user clicks a target whose section is too small to reach the configured line, an
 * adaptive scan line is temporarily anchored at that section's center (capped at the configured
 * line) so neighbouring small sections become active sequentially as the user scrolls past them,
 * instead of skipping to whichever larger section happens to overlap the configured line.
 *
 * Recomputation is driven entirely by scroll-settle and resize — geometry is cheap for the
 * handful of sections a typical scroll-spy nav contains, and any IntersectionObserver-based
 * "perf gate" would fire continuously during scroll only to be no-op'd by the `isScrolling`
 * bail in `updateActiveState`.
 */
export const scrollSpyDirective = {
  /**
   * Options container
   */
  options: scrollSpyOptions,

  /**
   * @type {Map<string, object>}
   */
  container: new Map(),

  lastScrollTop: 0,

  /**
   * Initialize plugin for the given element or elements which match the selector
   *
   * @param {Element} element HTML DOM element
   * @param {object}  options Additional plugin options
   */
  init(element, options = {}) {
    const targetLinks = [...element.querySelectorAll('[amb-action="scroll_to_section"]')];

    if (targetLinks.length === 0) {
      return;
    }

    const observableSections = new Map();

    const state = {
      element,
      options,
      observableSections,
      targetLinks,
      activeEntry: null,
      adaptiveLineY: null,
      scheduledRaf: null,
      requestUpdate: null,
      clickListener: null,
      clickTarget: null
    };

    let index = 0;

    for (const targetLink of targetLinks) {
      const targetSelector = targetLink.dataset.target || targetLink.getAttribute('href');

      if (targetSelector) {
        const section = document.querySelector(targetSelector);

        if (section) {
          observableSections.set(section, { section, targetLink, index });
          index++;
        }
      }
    }

    state.clickListener = (event) => {
      const targetLink = event.target.closest('[amb-action="scroll_to_section"]');

      if (targetLink) {
        $(targetLink).closest('.collapse.show').collapse('hide');
      }

      if (targetLinks.includes(targetLink)) {
        state.clickTarget = targetLink;

        // Seed the active entry up-front so the direction guard has a stable anchor while the
        // smooth scroll is in flight. Synthesize a minimal entry when the link's section is not
        // observable (e.g. the href doesn't resolve to a DOM section) so downstream code can
        // treat it uniformly.
        state.activeEntry = Array.from(observableSections.values())
          .find((entry) => entry.targetLink === targetLink) ||
          { section: null, targetLink, index: -1 };

        // Instant visual feedback. We deliberately do NOT call `requestUpdate()` here — the
        // rAF would fire before the smooth scroll has moved the page, consuming `clickTarget`
        // against the section's pre-scroll geometry and engaging the adaptive scan line at
        // the wrong Y. Letting the scroll-settle handler drive the recompute ensures the
        // adaptive engagement uses the section's resting position. The URL update also runs
        // exactly once at scroll-settle.
        this.activeTarget(element, targetLink);
      }
    };

    state.requestUpdate = () => this.scheduleUpdate(state, element);

    element.addEventListener('click', state.clickListener, false);
    window.addEventListener('resize', state.requestUpdate, { passive: true });

    this.container.set(element.ambid, state);
    this.observeScrollDirection();

    // Prime the initial active state on the next animation frame, once layout has stabilised.
    this.scheduleUpdate(state, element);
  },

  /**
   * Destroy plugin for the given element
   *
   * @param {Element} element HTML DOM element
   */
  destroy(element) {
    const state = this.container.get(element.ambid);

    if (state) {
      if (state.scheduledRaf) {
        cancelAnimationFrame(state.scheduledRaf);
      }

      if (state.clickListener) {
        element.removeEventListener('click', state.clickListener, false);
      }

      if (state.requestUpdate) {
        window.removeEventListener('resize', state.requestUpdate);
      }

      $(element).off('shown.bs.collapse');

      this.container.delete(element.ambid);
    }
  },

  /**
   * Install the singleton scroll listener that tracks direction (consumed by the direction
   * guard) and triggers a recompute on every registered directive once scrolling settles.
   * Idempotent — only the first directive instance to mount installs the listener.
   */
  observeScrollDirection() {
    if (this.scrollDirection) {
      return;
    }

    let scrollTimer = 0;
    const triggerSettleUpdate = () => {
      for (const s of this.container.values()) {
        if (s.requestUpdate) {
          s.requestUpdate();
        }
      }
    };

    window.addEventListener('scroll', () => {
      const lastScrollTop = this.lastScrollTop;

      this.lastScrollTop = scrollY;
      this.scrollDirection = scrollY > lastScrollTop ? 'down' : 'up';
      this.isScrolling = true;

      clearTimeout(scrollTimer);

      scrollTimer = setTimeout(() => {
        this.isScrolling = false;
        triggerSettleUpdate();
      }, 25);
    }, { passive: true });
  },

  /**
   * Coalesce active-state recomputations into a single rAF tick.
   *
   * @param {object} state
   * @param {Element} element
   */
  scheduleUpdate(state, element) {
    if (state.scheduledRaf) {
      return;
    }

    state.scheduledRaf = requestAnimationFrame(() => {
      state.scheduledRaf = null;
      this.updateActiveState(element);
    });
  },

  /**
   * Resolve the visible viewport, excluding sticky chrome via `scroll-padding-top` /
   * `scroll-padding-bottom` on the document root.
   *
   * @returns {{ top: number, bottom: number, height: number }}
   */
  getVisibleViewport() {
    const cs = getComputedStyle(document.documentElement);
    const top = parseFloat(cs.scrollPaddingTop) || 0;
    const bottom = parseFloat(cs.scrollPaddingBottom) || 0;
    const vh = window.innerHeight;
    const usableBottom = vh - bottom;

    return {
      top,
      bottom: Math.max(top, usableBottom),
      height: Math.max(0, usableBottom - top)
    };
  },

  /**
   * Resolve the Y coordinate (in viewport space) of the scan line.
   *
   * @param {*} option scanLine option (number | percent string | 'auto' | function)
   * @param {{top:number, bottom:number, height:number}} visible
   * @returns {number}
   */
  resolveScanLineY(option, visible) {
    if (typeof option === 'function') {
      return option(visible);
    }

    if (option === 'auto') {
      return visible.top;
    }

    if (typeof option === 'number') {
      return visible.top + option;
    }

    if (typeof option === 'string' && /%$/.test(option)) {
      const pct = parseFloat(option) / 100;

      return visible.top + visible.height * pct;
    }

    return visible.top + visible.height * 0.5;
  },

  /**
   * Compute the visible (clipped) rect of a section against the visible viewport. Returns null
   * when the section has no overlap with the visible area (e.g. fully behind the sticky header).
   *
   * @param {Element} section
   * @param {{top:number, bottom:number}} visible
   * @returns {{ top:number, bottom:number, center:number, height:number }|null}
   */
  getVisibleRect(section, visible) {
    const r = section.getBoundingClientRect();
    const top = Math.max(r.top, visible.top);
    const bottom = Math.min(r.bottom, visible.bottom);

    if (bottom <= top) {
      return null;
    }

    return { top, bottom, center: (top + bottom) / 2, height: bottom - top };
  },

  /**
   * Distance from a clipped rect to the scan line, per strategy.
   *
   * @param {{top:number, bottom:number, center:number}} rect
   * @param {number} lineY
   * @param {'center'|'edge'} strategy
   * @returns {number}
   */
  distanceToScanLine(rect, lineY, strategy) {
    if (strategy === 'edge') {
      return Math.min(Math.abs(rect.top - lineY), Math.abs(rect.bottom - lineY));
    }

    return Math.abs(rect.center - lineY);
  },

  /**
   * Run the geometric scan-line pick at the given Y coordinate (in viewport space) against the
   * current set of observable sections.
   *
   *   1. Build the candidate set from sections that are at least partly inside the visible
   *      viewport (clipped rect non-null).
   *   2. If any candidate overlaps `lineY`, restrict to that subset.
   *   3. Choose the candidate with the smallest distance to `lineY` per `strategy`.
   *
   * @param {object} state
   * @param {{top:number, bottom:number, height:number}} visible
   * @param {number} lineY
   * @param {'center'|'edge'} strategy
   * @returns {object|null} The chosen observable section entry, or null when nothing is visible.
   */
  pickAtLine(state, visible, lineY, strategy) {
    const candidates = [];
    const overlapping = [];

    for (const entry of state.observableSections.values()) {
      const rect = this.getVisibleRect(entry.section, visible);

      if (!rect) {
        continue;
      }

      const item = { entry, rect };

      candidates.push(item);

      if (rect.top <= lineY && lineY <= rect.bottom) {
        overlapping.push(item);
      }
    }

    const pool = overlapping.length > 0 ? overlapping : candidates;

    if (pool.length === 0) {
      return null;
    }

    let best = pool[0];
    let bestDistance = this.distanceToScanLine(best.rect, lineY, strategy);

    for (let i = 1; i < pool.length; i++) {
      const distance = this.distanceToScanLine(pool[i].rect, lineY, strategy);

      if (distance < bestDistance) {
        best = pool[i];
        bestDistance = distance;
      }
    }

    return best.entry;
  },

  /**
   * Pick the active section using the scan-line model:
   *   1. Pin to the first/last section unconditionally when the page is scrolled to the very top
   *      or bottom (when enabled). Prevents weird highlights on page load when small leading
   *      sections do not reach the configured scan line, and mirrors the same intent at the
   *      bottom of the document.
   *   2. Resolve the active line — the adaptive line if engaged (post-click on a small section),
   *      otherwise the configured line.
   *   3. Run the geometric pick at the active line.
   *   4. If adaptive is engaged and the configured line would now resolve the same section,
   *      release adaptive (lines have converged on a section large enough to span both).
   *   5. Apply the direction guard against backward jumps from layout shifts.
   *
   * @param {object} state
   * @returns {object|null} The chosen observable section entry
   */
  pickActiveEntry(state) {
    const { observableSections, options } = state;
    const sections = Array.from(observableSections.values());

    if (sections.length === 0) {
      return null;
    }

    const docHeight = document.documentElement.scrollHeight;
    const atBottom = scrollY + innerHeight >= docHeight - 1;
    const atTop = scrollY <= 0;

    // Edge pins run before the geometric pick so they always win at the document extremes —
    // prevents the "second section is active at scrollY=0" glitch caused by a small first
    // section that never overlaps the scan line. Also force-release any adaptive engagement so
    // the configured line takes back over once the user leaves the extreme.
    if (options.pinFirstAtTop && atTop) {
      state.adaptiveLineY = null;

      return sections[0];
    }

    if (options.pinLastAtBottom && atBottom) {
      state.adaptiveLineY = null;

      return sections[sections.length - 1];
    }

    const visible = this.getVisibleViewport();
    const configuredY = this.resolveScanLineY(options.scanLine, visible);
    const strategy = options.activeStrategy === 'edge' ? 'edge' : 'center';
    const adaptiveActive = state.adaptiveLineY != null;
    const lineY = adaptiveActive ? state.adaptiveLineY : configuredY;

    const pick = this.pickAtLine(state, visible, lineY, strategy);

    if (!pick) {
      return null;
    }

    // Convergence check — release adaptive once the configured line resolves the same section
    // as the adaptive line. This happens naturally as soon as a section large enough to span
    // both lines comes into view, after which the configured line takes over for the rest of
    // the scroll session.
    if (adaptiveActive) {
      const configuredPick = this.pickAtLine(state, visible, configuredY, strategy);

      if (configuredPick && configuredPick.section === pick.section) {
        state.adaptiveLineY = null;
      }
    }

    const previous = state.activeEntry;

    // Direction guard — defence-in-depth against backward jumps caused by mid-scroll layout
    // shifts (the click-induced small-section skip is handled by the adaptive line above).
    if (options.oppositeGuard && previous && previous.index >= 0) {
      if (this.scrollDirection === 'up' && pick.index > previous.index) {
        return previous;
      }

      if (this.scrollDirection === 'down' && pick.index < previous.index) {
        return previous;
      }
    }

    return pick;
  },

  /**
   * Recompute and apply the active state for the given directive instance.
   *
   * @param {Element} element Container element
   */
  updateActiveState(element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    if (this.isScrolling) {
      // Defer recomputation while the user (or browser) is actively scrolling. The global
      // scroll-settle handler in `observeScrollDirection` will trigger another update once
      // scrolling stops, at which point the geometric calculation runs against a stable layout.
      // The click handler has already toggled the `active` class for instant visual feedback,
      // so the user is not left without a highlighted link during the smooth scroll.
      return;
    }

    const clickTarget = state.clickTarget;
    let activeEntry = null;

    if (clickTarget) {
      // When a click target is pinned, the seeded `state.activeEntry` (set on click) is the
      // single source of truth. Release the pin so future scrolls are decided purely by geometry.
      activeEntry = state.activeEntry;
      state.clickTarget = null;
      state.adaptiveLineY = null;

      // Engage the adaptive scan line when the clicked section is too small / too high to reach
      // the configured line. We measure now (at scroll-settle) so the section's clipped rect
      // reflects its post-scroll resting position. The adaptive line is anchored at
      // `min(configured, section.center)` so it never moves below where the developer asked the
      // resting line to be — only pulled up toward small sections that need help.
      if (state.options.adaptiveScanLine && activeEntry && activeEntry.section) {
        const visible = this.getVisibleViewport();
        const configuredY = this.resolveScanLineY(state.options.scanLine, visible);
        const rect = this.getVisibleRect(activeEntry.section, visible);

        if (rect && !(rect.top <= configuredY && configuredY <= rect.bottom)) {
          state.adaptiveLineY = Math.min(configuredY, rect.center);
        }
      }
    }

    if (!activeEntry) {
      activeEntry = this.pickActiveEntry(state);
    }

    if (!activeEntry) {
      // No candidate and no fallback — keep the previous active state to avoid UI churn.
      return;
    }

    state.activeEntry = activeEntry;
    this.activeTarget(element, activeEntry.targetLink);

    // Debug visualisation — kept here (not inside `pickActiveEntry`) so the line is rewritten
    // on every recompute path, including click-settle which short-circuits the geometric pick.
    if (element.classList.contains('debug')) {
      const visible = this.getVisibleViewport();
      const lineY = state.adaptiveLineY != null ?
        state.adaptiveLineY :
        this.resolveScanLineY(state.options.scanLine, visible);

      element.style.setProperty('--scan-line', `${lineY}px`);
    }

    if (state.options.updateUrl) {
      const hash = activeEntry.targetLink.getAttribute('href');
      const historyMethod = `${state.options.updateUrl.toLowerCase()}State`;

      if (hash && (clickTarget || scrollY > innerHeight)) {
        if (hash !== window.location.hash) {
          window.history[historyMethod]({ target: hash }, null, hash);
        }
      } else {
        window.history[historyMethod](
          { target: null },
          null,
          window.location.pathname + window.location.search
        );
      }
    }
  },

  /**
   * Reflect the given target link as the active one in the navbar UI:
   *   - toggle the `active` class across all scroll-spy nav links (only the given one wins)
   *   - move the active-link indicator (CSS custom properties on the `.scroll-spy` group)
   *   - mirror the active link's text into the matching `.navbar-toggler-text` (mobile collapse)
   *
   * @param {Element} element    Container element (directive root)
   * @param {Element} targetLink The nav link to mark as active
   */
  activeTarget(element, targetLink) {
    for (const link of element.querySelectorAll('[amb-action="scroll_to_section"]')) {
      link.classList.toggle('active', link === targetLink);
    }

    const group = targetLink.closest('.scroll-spy');

    if (!group) {
      return;
    }

    const { offsetTop, offsetLeft, offsetWidth, offsetHeight } = targetLink;
    const navbarTogglerText = Array.from(element.querySelectorAll('[data-toggle="collapse"]'))
      .find((collapseButton) => group.matches(collapseButton.dataset.target))
      ?.querySelector('.navbar-toggler-text');

    if (navbarTogglerText) {
      navbarTogglerText.textContent = targetLink.textContent.trim();
    }

    group.style.setProperty('--active-link-top', `${offsetTop + offsetHeight}px`);
    group.style.setProperty('--active-link-left', `${offsetLeft}px`);
    group.style.setProperty('--active-link-width', `${offsetWidth}px`);
  }
};
